import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import serviceCoverPageImg from "../../assets/serviceCoverPageImg.png";

type Props = {
  sectionNumber?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  companyName?: string;
};

const SectionCoverPage = ({
  sectionNumber = "4",
  sectionTitle = "Services",
  sectionDescription = "An overview of your personalized health optimization program, including service details, scheduling, and comprehensive wellness protocols.",
  companyName = "PNOE",
}: Props) => {
  const theme: Theme = useTheme();

  return (
    <Box sx={styles.pageContainer}>
      {/* Background Image */}
      <Box
        component="img"
        src={serviceCoverPageImg}
        alt="Cover"
        sx={styles.backgroundImage}
      />

      {/* Large Section Number */}
      <Box component="p" sx={styles.sectionNumber}>
        {sectionNumber}
      </Box>

      {/* Section Content (right side) */}
      <Box sx={styles.sectionContent}>
        {/* Horizontal line */}
        <Box sx={styles.horizontalLine(theme)} />

        {/* Section Title */}
        <Box component="p" sx={styles.sectionTitle(theme)}>
          {sectionTitle}
        </Box>

        {/* Section Description */}
        <Box component="p" sx={styles.sectionDescription(theme)}>
          {sectionDescription}
        </Box>
      </Box>

      {/* Footer - Company Name */}
      <Box sx={styles.footer}>
        <Box component="p" sx={styles.companyName(theme)}>
          {companyName}
        </Box>
      </Box>
    </Box>
  );
};

export default SectionCoverPage;

const styles = {
  pageContainer: {
    width: "595px", // A4 width in points for print/PDF
    height: "843px", // A4 height in points for print/PDF
    position: "relative" as "relative",
    overflow: "hidden",
    borderBottom: "1px solid #F7F9FA",
  },
  backgroundImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as "cover",
    position: "absolute" as "absolute",
    top: 0,
    left: 0,
  },
  sectionNumber: {
    position: "absolute" as "absolute",
    zIndex: 2,
    left: "20px",
    top: "-130px",
    fontFamily: "var(--font-Medium)",
    fontSize: "350px",
    lineHeight: "normal",
    color: "#BAC2CE",
    margin: 0,
  },
  sectionContent: {
    position: "absolute" as "absolute",
    zIndex: 2,
    left: "323px",
    top: "652px",
    width: "236px",
  },
  horizontalLine: (theme: Theme) => ({
    width: "29.732px",
    height: "3px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    marginBottom: "8px",
  }),
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "17px",
    lineHeight: "normal",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    margin: "0 0 8px 0",
  }),
  sectionDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    margin: 0,
    whiteSpace: "pre-wrap" as "pre-wrap",
  }),
  footer: {
    position: "absolute" as "absolute",
    zIndex: 2,
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "595px",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
  },
  companyName: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "normal",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    margin: 0,
  }),
};
